import axios from 'axios'
import { useMutation } from '@tanstack/react-query'

import type {
  TNewUploadAnalysedFile,
  TNewUploadFile,
  TNewUploadManualFileOptions,
  TNewUploadManualTuneRequired,
  TNewUploadOriginalUpload,
  TUploadType,
} from './useNewUpload'
import { PrivateAPI } from '../utils/api'

type TCreateFileResponse = {
  file: {
    id: string
  }
  url: string
}

type TSaveBuildManualResponse = {
  analysedFile: TNewUploadAnalysedFile
}

type TSaveBuildManualMutationInput = {
  analysedFileId: string
  fileOptions: TNewUploadManualFileOptions
  files: TNewUploadFile[]
  originalUpload: TNewUploadOriginalUpload | null
  tuneRequired: TNewUploadManualTuneRequired
  tuneRequiredCustom: string
  uploadType: TUploadType
}

const getSaveBuildManualErrorMessage = (err: unknown) => {
  const defaultMessage = 'An unexpected error occured'

  if (!axios.isAxiosError(err)) {
    return `${defaultMessage} [990]`
  }

  const status = err.response?.status

  if (!status) {
    return `${defaultMessage} [990]`
  }

  if (status >= 400 && status < 500) {
    return `${defaultMessage} [${status}]`
  }

  return defaultMessage
}

const useSaveBuildManual = () => {
  return useMutation<
    TSaveBuildManualResponse,
    Error,
    TSaveBuildManualMutationInput
  >({
    mutationKey: ['save-build-manual'],
    mutationFn: async ({
      analysedFileId,
      fileOptions,
      files,
      originalUpload,
      tuneRequired,
      tuneRequiredCustom,
      uploadType,
    }) => {
      try {
        const uploadedFileIds = await Promise.all(
          files.map(async (file) => {
            if (file.id && !file.fileBlob) {
              return file.id
            }

            if (!file.fileBlob) {
              throw new Error('Invalid manual file')
            }

            const createFileResponse = await PrivateAPI<TCreateFileResponse>({
              url: '/files',
              method: 'post',
              data: {
                name: file.name,
                size: file.size,
                bucket: 'UPLOADS',
              },
            })

            await axios({
              url: createFileResponse.data.url,
              method: 'put',
              data: file.fileBlob,
              headers: file.fileBlob.type
                ? { 'Content-Type': file.fileBlob.type }
                : undefined,
            })

            return createFileResponse.data.file.id
          }),
        )

        const saveResponse = await PrivateAPI<TSaveBuildManualResponse>({
          url: `/uploads/analyse/${analysedFileId}/manual`,
          method: 'patch',
          data: {
            manual: {
              files: uploadedFileIds,
              originalUpload: originalUpload?.id,
              tuneRequired,
              tuneRequiredCustom,
              uploadType,
              fileOptions,
            },
          },
        })

        return {
          analysedFile: saveResponse.data.analysedFile,
        }
      } catch (err) {
        throw new Error(getSaveBuildManualErrorMessage(err))
      }
    },
  })
}

export default useSaveBuildManual
